import { useState, useEffect, useRef } from "react";
import { makeTr } from "@/i18n/tr";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/store/useStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, Users, Clock, Coins, Swords, X, Bot, Zap } from "lucide-react";
import { API_BASE as API_URL } from "@/config/api";

// Stake options in points (1 Point = 1 RUB)
const STAKE_OPTIONS = [0, 50, 100, 300, 500, 1000, 3000];
const TIME_CONTROLS = [
  { value: 60, label: "1 min", type: "Bullet" },
  { value: 180, label: "3 min", type: "Bullet" },
  { value: 300, label: "5 min", type: "Blitz" },
  { value: 600, label: "10 min", type: "Rapid" },
  { value: 900, label: "15 min", type: "Rapid" },
];

// Timeout before showing "Play with Bot" option (10 seconds)
const BOT_OFFER_TIMEOUT = 10;

export default function MatchmakingPage() {
  const navigate = useNavigate();
  const { user, token } = useStore();
  const { i18n } = useTranslation();
  const tr = makeTr(i18n.language);
  const [stake, setStake] = useState(0);
  const [timeControl, setTimeControl] = useState(600);
  const [inQueue, setInQueue] = useState(false);
  const [searching, setSearching] = useState(false);
  const [queueSize, setQueueSize] = useState(0);
  const [searchTime, setSearchTime] = useState(0);
  const [showBotOffer, setShowBotOffer] = useState(false);
  const queueIdRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    checkActiveGame();
    axios.get(`${API_URL}/matchmaking/queue`, { headers })
      .then((res) => setQueueSize(res.data.total_in_queue || 0))
      .catch(() => {});
    return () => {
      if (queueIdRef.current) {
        axios.delete(`${API_URL}/matchmaking/leave`, { headers }).catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    let interval;
    if (inQueue) {
      interval = setInterval(() => {
        setSearchTime((t) => t + 1);
        pollMatchStatus();
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [inQueue]);

  const checkActiveGame = async () => {
    try {
      const res = await axios.get(`${API_URL}/games/active`, { headers });
      if (res.data.has_active_game) {
        toast.info(tr("У вас есть активная партия", "You have an active game"));
        navigate(`/game/${res.data.game.id}`);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const pollMatchStatus = async () => {
    const queueId = queueIdRef.current;
    if (!queueId) return;
    try {
      const res = await axios.get(`${API_URL}/matchmaking/status/${queueId}`, { headers });
      if (res.data.status === "matched" && res.data.game_id) {
        queueIdRef.current = null;
        setInQueue(false);
        toast.success(tr("Соперник найден!", "Opponent found!"));
        navigate(`/game/${res.data.game_id}`);
      } else if (res.data.status === "timeout" || res.data.offer_bot) {
        setShowBotOffer(true);
      }
      if (res.data.queue_size !== undefined) setQueueSize(res.data.queue_size);
    } catch (err) {
      if (err.response?.status === 404) {
        queueIdRef.current = null;
        setInQueue(false);
      }
    }
  };

  const joinQueue = async () => {
    if (stake > 0 && user?.balance < stake) {
      toast.error(tr("Недостаточно средств", "Insufficient balance"));
      return;
    }

    setSearching(true);
    try {
      const res = await axios.post(
        `${API_URL}/matchmaking/join`,
        { stake, time_control: timeControl, increment: 0 },
        { headers }
      );
      if (res.data.status === "matched") {
        toast.success(tr("Соперник найден!", "Opponent found!"));
        navigate(`/game/${res.data.game_id}`);
      } else if (res.data.status === "searching" || res.data.status === "already_searching") {
        queueIdRef.current = res.data.queue_id;
        setInQueue(true);
        setSearchTime(0);
        setShowBotOffer(false);
        toast.info(tr("Ищем соперника...", "Searching for an opponent..."));
      } else {
        toast.error(res.data.message || tr("Не удалось начать поиск", "Could not start search"));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Не удалось начать поиск", "Could not start search"));
    }
    setSearching(false);
  };

  const leaveQueue = async () => {
    try {
      await axios.delete(`${API_URL}/matchmaking/leave`, { headers });
    } catch (err) {
      console.error(err);
    }
    queueIdRef.current = null;
    setInQueue(false);
    setSearchTime(0);
    setShowBotOffer(false);
    toast.info(tr("Поиск отменён", "Search cancelled"));
  };

  const playWithBot = async () => {
    const queueId = queueIdRef.current;
    setSearching(true);
    try {
      const res = queueId
        ? await axios.post(`${API_URL}/matchmaking/play-bot?queue_id=${queueId}`, {}, { headers })
        : await axios.post(`${API_URL}/matchmaking/quick-play`, { stake: 0, time_control: timeControl, increment: 0, prefer_human: false }, { headers });
      if (res.data.success) {
        const bot = res.data.bot || res.data.opponent;
        toast.success(`{tr("Играем против", "Playing against")} ${bot?.username || "Bot"} ({tr("Бот", "Bot")})`);
        queueIdRef.current = null;
        setInQueue(false);
        setShowBotOffer(false);
        navigate(`/game/${res.data.game_id}`);
      } else {
        toast.error(res.data.message || tr("Не удалось начать игру с ботом", "Could not start a bot game"));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Не удалось начать игру с ботом", "Could not start a bot game"));
    }
    setSearching(false);
  };

  const quickPlay = async () => {
    setSearching(true);
    try {
      const res = await axios.post(
        `${API_URL}/matchmaking/quick-play`,
        { stake, time_control: timeControl, increment: 0, prefer_human: true },
        { headers }
      );
      if (res.data.success && res.data.game_id) {
        const opponentType = res.data.opponent_type === "bot" ? ` (tr("Бот", "Bot"))` : "";
        toast.success(`{tr("Играем против", "Playing against")} ${res.data.opponent?.username}${opponentType}`);
        navigate(`/game/${res.data.game_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Быстрая игра недоступна", "Quick play unavailable"));
    }
    setSearching(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const selectedTimeControl = TIME_CONTROLS.find((tc) => tc.value === timeControl);

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl" data-testid="matchmaking-page">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">{tr("Игра на деньги", "Play for Money")}</h1>
        <p className="text-muted-foreground">{tr("Найдите соперника и сыграйте на реальную ставку", "Find an opponent and compete for real stakes")}</p>
      </div>

      {/* Queue Status */}
      <Card className="mb-6 bg-card/50 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center gap-6">
            <div className="text-center">
              <Users className="h-6 w-6 mx-auto mb-1 text-primary" />
              <p className="text-2xl font-bold">{queueSize}</p>
              <p className="text-xs text-muted-foreground">{tr("Игроков онлайн", "Players Online")}</p>
            </div>
            <div className="h-12 w-px bg-border" />
            <div className="text-center">
              <Coins className="h-6 w-6 mx-auto mb-1 text-yellow-500" />
              <p className="text-2xl font-bold">{user?.balance?.toFixed(2) || "0.00"} P</p>
              <p className="text-xs text-muted-foreground">{tr("Ваш баланс (Points)", "Your Balance (Points)")}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {inQueue ? (
        /* Searching State */
        <Card className="border-primary animate-pulse">
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              <div className="relative inline-block mb-4">
                <Loader2 className="h-16 w-16 animate-spin text-primary" />
                <Swords className="h-8 w-8 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-primary" />
              </div>
              <h2 className="text-xl font-bold mb-2">{tr("Ищем соперника...", "Searching for opponent...")}</h2>
              <p className="text-muted-foreground mb-4">
                {selectedTimeControl?.type} • {stake > 0 ? `${stake} Points` : tr("Бесплатная игра", "Free game")}
              </p>
              <p className="text-3xl font-mono mb-6">{formatTime(searchTime)}</p>
              
              {/* Show bot offer after timeout */}
              {showBotOffer && (
                <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-400 mb-3">
                    {tr("Соперник не найден. Сыграть с ботом вашего уровня?", "No opponents found. Play against a bot at your level?")}
                  </p>
                  <Button 
                    variant="secondary" 
                    onClick={playWithBot}
                    disabled={searching}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Bot className="h-4 w-4 mr-2" />
                    {tr("Играть с ботом", "Play with Bot")}
                  </Button>
                </div>
              )}
              
              <Button variant="destructive" onClick={leaveQueue}>
                <X className="h-4 w-4 mr-2" />
                {tr("Отменить поиск", "Cancel Search")}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Game Setup */
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Swords className="h-5 w-5" />
              {tr("Настройки игры", "Game Settings")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Quick Play Button */}
            <div className="p-4 bg-gradient-to-r from-primary/20 to-primary/5 rounded-lg border border-primary/30">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <Zap className="h-4 w-4 text-yellow-500" />
                    {tr("Быстрая игра", "Quick Play")}
                  </h3>
                  <p className="text-sm text-muted-foreground">{tr("Мгновенный матч — человек или бот", "Instant match - human or bot")}</p>
                </div>
                <Button onClick={quickPlay} disabled={searching}>
                  {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : tr("Играть сейчас", "Play Now")}
                </Button>
              </div>
            </div>

            {/* Time Control */}
            <div>
              <label className="text-sm font-medium mb-2 block">{tr("Контроль времени", "Time Control")}</label>
              <div className="grid grid-cols-5 gap-2">
                {TIME_CONTROLS.map((tc) => (
                  <Button
                    key={tc.value}
                    variant={timeControl === tc.value ? "default" : "outline"}
                    className="flex flex-col h-auto py-3"
                    onClick={() => setTimeControl(tc.value)}
                    data-testid={`time-control-${tc.value}`}
                    size="sm"
                  >
                    <Clock className="h-4 w-4 mb-1" />
                    <span className="font-bold text-xs">{tc.label}</span>
                    <span className="text-xs opacity-70">{tc.type}</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Stake Selection */}
            <div>
              <label className="text-sm font-medium mb-2 block">{tr("Ставка (Points, 1 P = 1 ₽)", "Stake (Points, 1 P = 1 ₽)")}</label>
              <div className="grid grid-cols-4 gap-2">
                {STAKE_OPTIONS.map((s) => (
                  <Button
                    key={s}
                    variant={stake === s ? "default" : "outline"}
                    className={`${s > 0 && user?.balance < s ? "opacity-50" : ""}`}
                    onClick={() => setStake(s)}
                    disabled={s > 0 && user?.balance < s}
                    data-testid={`stake-${s}`}
                    size="sm"
                  >
                    {s === 0 ? tr("Бесплатно", "Free") : `${s} P`}
                  </Button>
                ))}
              </div>
              {stake > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Winner takes: {(stake * 2 * 0.95).toFixed(2)} Points (5% platform fee)
                </p>
              )}
            </div>

            {/* Play Button */}
            <Button
              size="lg"
              className="w-full text-lg h-14"
              onClick={joinQueue}
              disabled={searching || (stake > 0 && user?.balance < stake)}
              data-testid="find-match-btn"
            >
              {searching ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Swords className="h-5 w-5 mr-2" />
                  {stake > 0 ? `${tr("Играть на", "Play for")} ${stake} Points` : tr("Играть бесплатно", "Play Free Game")}
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Quick Info */}
      <div className="mt-6 grid grid-cols-3 gap-4 text-center text-sm text-muted-foreground">
        <div>
          <Badge variant="outline">{tr("Честный подбор", "Fair Matching")}</Badge>
          <p className="mt-1">{tr("±200 рейтинга", "±200 rating range")}</p>
        </div>
        <div>
          <Badge variant="outline">{tr("Комиссия 5%", "5% Fee")}</Badge>
          <p className="mt-1">{tr("Комиссия платформы", "Platform commission")}</p>
        </div>
        <div>
          <Badge variant="outline">Anti-Cheat</Badge>
          <p className="mt-1">{tr("ИИ-мониторинг", "AI monitoring")}</p>
        </div>
      </div>
    </div>
  );
}
